import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { dictionaries, LANGUAGES, type Lang } from './translations';

type Params = Record<string, string | number | null | undefined>;

interface I18nContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Translate a key. Pass params to interpolate `{name}` tokens. */
  t: (key: string, params?: Params) => string;
  languages: typeof LANGUAGES;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

function getInitialLang(): Lang {
  const stored = localStorage.getItem('lang') as Lang | null;
  if (stored === 'fr' || stored === 'en') return stored;
  return 'fr';
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    localStorage.setItem('lang', lang);
  }, [lang]);

  const setLang = useCallback((next: Lang) => setLangState(next), []);

  const t = useCallback(
    (key: string, params?: Params) => {
      const dict = dictionaries[lang];
      let s = dict[key] ?? dictionaries.fr[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          s = s.split(`{${k}}`).join(v == null ? '' : String(v));
        }
      }
      return s;
    },
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useT must be used within I18nProvider');
  return ctx;
}

/**
 * Locale-aware date formatting hook. Formats ISO date/timestamp strings using
 * the active language's Swiss locale (fr-CH / en-CH). Returns a stable set of
 * formatters so components format dates consistently across the app.
 */
export function useFormatDate() {
  const { lang } = useT();
  const locale = lang === 'fr' ? 'fr-CH' : 'en-CH';

  const toDate = (iso: string): Date | null => {
    if (!iso) return null;
    // Date-only "YYYY-MM-DD" → construct in local time to avoid TZ drift.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  return {
    locale,
    /** Long form: "lundi 16 juillet 2026". */
    long: (iso: string) => {
      const d = toDate(iso);
      return d
        ? d.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
        : iso;
    },
    /** Medium form: "16 juil. 2026". */
    medium: (iso: string) => {
      const d = toDate(iso);
      return d ? d.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' }) : iso;
    },
    /** Short numeric form: "16.07.2026" (fr) / "07/16/2026" (en). */
    short: (iso: string) => {
      const d = toDate(iso);
      return d ? d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : iso;
    },
    /** Month + year: "juillet 2026". */
    monthYear: (iso: string) => {
      const d = toDate(iso);
      return d ? d.toLocaleDateString(locale, { month: 'long', year: 'numeric' }) : iso;
    },
    /** Date + time: "16 juil. 2026, 14:30". */
    dateTime: (iso: string) => {
      const d = toDate(iso);
      return d
        ? d.toLocaleString(locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        : iso;
    },
  };
}

export type { Lang };
